import React, { useEffect, useState, useCallback } from 'react';
import { useAxios } from '../../Helpers/AxiosHelper';
import {
    Card,
    Column,
    DataTable,
    SCIcon,
    Tree,
    TreeNode
} from '../../Common/Imports';
import { ApiResponse, IdentityRole, Items_EXT } from '../../Models/Classes';
import { TreeSelectionEvent } from 'primereact/tree';
import { useTranslation } from 'react-i18next';
import PermissionsList from './Permissions_List';

export const RolesList: React.FC = () => {
    const [RoleListData, SetRoleListData] = useState<TreeNode[]>([]);
    const [RoleData, SetRoleData] = useState<Items_EXT[]>([]);
    const [selectedKey, setSelectedKey] = useState<string | undefined>(undefined);
    const [selectedItem, setSelectedItem] = useState<IdentityRole | undefined>(undefined);
    const [expandedRows, setExpandedRows] = useState<Items_EXT[] | undefined>(undefined);
    const AxiosHelper = useAxios();
    const { t } = useTranslation();

    const GetData = useCallback(async () => {
        try {
            const resp = await AxiosHelper.get<ApiResponse<TreeNode[]>>(`Auth/GetRoles`);
            if (resp.data.code === 0) {
                SetRoleListData(resp.data.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    }, [AxiosHelper]);

    useEffect(() => {
        GetData();
    }, [GetData]);

    const GetRoleData = useCallback(async () => {
        if (!selectedKey) return;
        try {
            const resp = await AxiosHelper.get<ApiResponse<Items_EXT[]>>(`Auth/GetItems/${selectedKey}`);
            if (resp.data.code === 0) {
                SetRoleData(resp.data.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    }, [AxiosHelper, selectedKey]);

    useEffect(() => {
        if (selectedKey) {
            GetRoleData();
        }
    }, [GetRoleData, selectedKey]);

    const nodeTemplate = (node: TreeNode) => {
        return (<>
            <SCIcon Name="Roles" Classes="me-3" />
            {node.label}
        </>);
    }

    const SetTreeSelection = (key: string) => {
        const _T = RoleListData.find(m => m.key == key)?.data;
        if (_T) {
            setSelectedItem(_T);
            setSelectedKey(key);
        }
    };

    const handleSelectionChange = (e: TreeSelectionEvent) => {
        const selectedNodeKey = e.value as string | undefined;
        if (selectedNodeKey) {
            SetTreeSelection(selectedNodeKey);
        }
    };

    const rowExpansionTemplate = (data: Items_EXT) => {
        return <PermissionsList IDAspNetRole={selectedKey as string} IDItem={data.idItem as string} />;
    };

    return (
        <div className="row">
            <div className="col-md-3">
                <Tree
                    value={RoleListData}
                    nodeTemplate={nodeTemplate}
                    className="w-full"
                    selectionMode="single"
                    selectionKeys={selectedKey}
                    onSelectionChange={handleSelectionChange}
                />
            </div>
            {
                RoleData && RoleData.length > 0 && (
                    <div className="col-md-9">
                        <Card title={selectedItem?.name}>
                            <DataTable
                                paginator
                                rows={10}
                                rowsPerPageOptions={[10, 20, 50]}
                                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
                                paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                value={RoleData}
                                size="small"
                                stripedRows
                                expandedRows={expandedRows}
                                onRowToggle={(e) => { setExpandedRows(e.data as Items_EXT[]) }}
                                rowExpansionTemplate={rowExpansionTemplate}
                                tableStyle={{ minWidth: '50rem' }}>
                                <Column
                                    filter
                                    field="idItem"
                                    bodyClassName="table-cell-nowrap" />
                                <Column
                                    filter
                                    field="label"
                                    bodyClassName="table-cell-nowrap" />
                                <Column
                                    filter
                                    field="label"
                                    body={(e: Items_EXT) => { return t(e.label) }}
                                    bodyClassName="table-cell-nowrap" />
                                <Column
                                    expander={(e: Items_EXT) => {
                                        if (e.itemsPermissions && e.itemsPermissions.length > 0) {
                                            return true
                                        } else {
                                            return false
                                        }
                                    }}
                                    style={{ width: '5rem' }}
                                />
                            </DataTable>
                        </Card>
                    </div>
                )
            }
        </div>
    );
};

export default RolesList;
